import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AppSelect } from '@/components/shared/AppSelect';
import { Badge } from '@/components/shared/Badge';
import { Button } from '@/components/shared/Button';
import { ConfirmationDialog } from '@/components/shared/ConfirmationDialog';
import { Panel } from '@/components/shared/Panel';
import { eventRoutes, templateRoutes } from '@/data/routes';
import { getTemplateSlotMetrics, createTemplateDraftFromState, cloneTemplateBuilderDraft } from '@/features/templates/builder';
import { useAuth } from '@/hooks/useAuth';
import { useGuildData } from '@/hooks/useGuildData';
import { deleteTemplateRecord, setTemplateActiveState } from '@/lib/repositories/templateRepository';
import type { PartyTemplate, PartyTemplateSlot, TemplateDraftRecord } from '@/types/domain';
import { formatDateTime } from '@/utils/date';

interface TemplateSummaryProps {
  templates: PartyTemplate[];
  templateSlots: PartyTemplateSlot[];
  templateDrafts: TemplateDraftRecord[];
  canManageTemplates: boolean;
}

type TemplateFilter = 'all' | 'active' | 'archived' | 'draft-available';

export function TemplateSummary({
  templates,
  templateSlots,
  templateDrafts,
  canManageTemplates,
}: TemplateSummaryProps): JSX.Element {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { state, refreshData } = useGuildData();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<TemplateFilter>('all');
  const [pendingArchiveId, setPendingArchiveId] = useState<string | null>(null);
  const [pendingDeleteTemplate, setPendingDeleteTemplate] = useState<PartyTemplate | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const normalizedQuery = query.trim().toLowerCase();
  const createDraft = templateDrafts.find((draft) => !draft.templateId) ?? null;
  const draftBackedTemplateIds = new Set(templateDrafts.map((draft) => draft.templateId).filter((value): value is string => Boolean(value)));

  const filteredTemplates = useMemo(
    () =>
      templates.filter((template) => {
        const matchesQuery =
          !normalizedQuery ||
          template.name.toLowerCase().includes(normalizedQuery) ||
          template.description.toLowerCase().includes(normalizedQuery);
        const matchesFilter =
          filter === 'all' ||
          (filter === 'active' && template.isActive) ||
          (filter === 'archived' && !template.isActive) ||
          (filter === 'draft-available' && draftBackedTemplateIds.has(template.id));

        return matchesQuery && matchesFilter;
      }),
    [draftBackedTemplateIds, filter, normalizedQuery, templates],
  );

  return (
    <Panel
      title="Reusable Mass Templates"
      description="Saved layouts, draft-backed edits, and direct mass creation all live here."
      action={
        canManageTemplates ? (
          <div className="panel-actions">
            <Link to={templateRoutes.create} className="button button--secondary">
              Create Template
            </Link>
          </div>
        ) : null
      }
    >
      <div className="form-grid form-grid--two">
        <label className="field">
          <span className="field__label">Search Templates</span>
          <input
            className="field__input"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by template name or note"
          />
        </label>

        <div className="field">
          <span className="field__label">Filter</span>
          <AppSelect
            ariaLabel="Template filter"
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'all', label: 'All Templates', description: 'Saved and archived layouts' },
              { value: 'active', label: 'Active Templates', description: 'Ready for mass creation' },
              { value: 'archived', label: 'Archived Templates', description: 'Hidden from normal mass creation' },
              { value: 'draft-available', label: 'Draft Available', description: 'Saved templates with working drafts' },
            ]}
          />
        </div>
      </div>

      {createDraft ? (
        <div className="template-summary__draft-callout">
          <div>
            <span className="profile-summary__label">Recovered New Template Draft</span>
            <strong>{createDraft.name || 'Untitled Working Draft'}</strong>
            <span className="profile-summary__hint">Last autosaved {formatDateTime(createDraft.lastAutosavedAt)}</span>
          </div>
          <Link to={templateRoutes.create} className="button button--secondary">
            Resume New Draft
          </Link>
        </div>
      ) : null}

      {filteredTemplates.length > 0 ? (
        <div className="template-grid">
          {filteredTemplates.map((template) => {
            const slots = templateSlots.filter((slot) => slot.templateId === template.id);
            const metrics = getTemplateSlotMetrics(slots);
            const hasDraft = draftBackedTemplateIds.has(template.id);

            return (
              <article key={template.id} className="template-card">
                <div className="template-card__header">
                  <div>
                    <strong>{template.name}</strong>
                    <p>{template.description || 'Reusable four-party layout.'}</p>
                  </div>
                  <div className="slot-card__badges">
                    <Badge tone={template.isActive ? 'success' : 'default'}>{template.isActive ? 'Active' : 'Archived'}</Badge>
                    {hasDraft ? <Badge tone="info">Draft Available</Badge> : null}
                  </div>
                </div>

                <div className="template-card__metrics">
                  <span>{metrics.configuredSlotCount} configured {metrics.configuredSlotCount === 1 ? 'slot' : 'slots'}</span>
                  <span>{metrics.activeSlotCount} active {metrics.activeSlotCount === 1 ? 'slot' : 'slots'}</span>
                  <span>{metrics.totalRequired} total required</span>
                  <span>{metrics.configuredPartyCount} {metrics.configuredPartyCount === 1 ? 'party used' : 'parties used'}</span>
                </div>

                <div className="template-card__meta">
                  <span>Updated {formatDateTime(template.updatedAt)}</span>
                  {!template.isActive ? <span>Archived templates stay out of normal mass creation.</span> : null}
                </div>

                <div className="panel-actions">
                  {canManageTemplates ? (
                    <>
                      <Link to={templateRoutes.edit(template.id)} className="button button--ghost">
                        Edit Template
                      </Link>
                      <Button
                        variant="secondary"
                        onClick={() => {
                          const savedDraft = createTemplateDraftFromState(state, template.id);

                          if (!savedDraft) {
                            return;
                          }

                          navigate(templateRoutes.create, {
                            state: {
                              initialTemplateDraft: cloneTemplateBuilderDraft(savedDraft, {
                                asNew: true,
                                nextName: `${template.name} Copy`,
                              }),
                              skipStoredCreateDraft: true,
                              sourceTemplateId: template.id,
                            },
                          });
                        }}
                      >
                        Duplicate
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setPendingArchiveId(template.id);
                        }}
                      >
                        {template.isActive ? 'Archive' : 'Reactivate'}
                      </Button>
                      <Button
                        variant="danger"
                        onClick={() => {
                          setPendingDeleteTemplate(template);
                          setDeleteConfirmation('');
                        }}
                      >
                        Delete Template
                      </Button>
                    </>
                  ) : null}
                  <Link
                    to={template.isActive ? eventRoutes.createFromTemplate(template.id) : '#'}
                    className={`button button--secondary${template.isActive ? '' : ' button--disabled-link'}`}
                    onClick={(event) => {
                      if (!template.isActive) {
                        event.preventDefault();
                      }
                    }}
                  >
                    {template.isActive ? 'Create Mass' : 'Archived'}
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <p className="copy-block">No templates matched the current search.</p>
      )}

      <ConfirmationDialog
        open={Boolean(pendingArchiveId)}
        title={templates.find((template) => template.id === pendingArchiveId)?.isActive ? 'Archive Template' : 'Reactivate Template'}
        description={
          templates.find((template) => template.id === pendingArchiveId)?.isActive
            ? 'Archived templates stay visible for management but no longer appear as normal choices when creating a mass.'
            : 'Reactivating this template makes it available again in Create Mass.'
        }
        confirmLabel={templates.find((template) => template.id === pendingArchiveId)?.isActive ? 'Archive Template' : 'Reactivate Template'}
        confirmVariant="secondary"
        confirmationValue=""
        onConfirmationValueChange={() => undefined}
        isProcessing={isProcessingAction}
        onClose={() => {
          if (isProcessingAction) {
            return;
          }

          setPendingArchiveId(null);
        }}
        onConfirm={() => {
          const target = templates.find((template) => template.id === pendingArchiveId);

          if (!target || !profile) {
            return;
          }

          setIsProcessingAction(true);

          void setTemplateActiveState(target.id, profile.id, !target.isActive)
            .then(() => refreshData())
            .finally(() => {
              setIsProcessingAction(false);
              setPendingArchiveId(null);
            });
        }}
      />

      <ConfirmationDialog
        open={Boolean(pendingDeleteTemplate)}
        title="Delete Template"
        description="Deleting a saved template is permanent. If masses already reference it, Woof Woof will archive it instead."
        confirmLabel="Delete Template"
        confirmVariant="danger"
        confirmationLabel="Type the template name to confirm"
        requiredText={pendingDeleteTemplate?.name ?? ''}
        confirmationValue={deleteConfirmation}
        onConfirmationValueChange={setDeleteConfirmation}
        isProcessing={isProcessingAction}
        onClose={() => {
          if (isProcessingAction) {
            return;
          }

          setPendingDeleteTemplate(null);
          setDeleteConfirmation('');
        }}
        onConfirm={() => {
          if (!pendingDeleteTemplate) {
            return;
          }

          setIsProcessingAction(true);

          void deleteTemplateRecord(pendingDeleteTemplate.id, deleteConfirmation)
            .then(() => refreshData())
            .finally(() => {
              setIsProcessingAction(false);
              setPendingDeleteTemplate(null);
              setDeleteConfirmation('');
            });
        }}
      />
    </Panel>
  );
}
